import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import classNames from "classnames";
import { Loading } from "components/panels";
import { PhosphorXButton } from "components/Atoms/PhosphorIconButton";
import { focusOnMount } from "utilities/reactUtils";

export interface InputBaseProps {
  value: string
  onChange: (newValue: string) => void

  maxLength?: number
  onBlur?: (newValue: string) => void
  onEnter?: () => void
  onEsc?: () => void
  onFocus?: () => void
  onClick?: () => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void

  type?: "text" | "password" | "email" | "url"
  placeholder?: string
  disabled?: boolean
  error?: string | boolean
  loading?: boolean
  focusOnMount?: boolean
  clearable?: boolean
  onClear?: () => void
  width?: number
  rightComponent?: InputBaseRightComponent;

  id?: string
  name?: string
  autoComplete?: string
  autoCapitalize?: "none" | "sentences" | "words" | "characters"
}

export type InputBaseRightComponent = InputBaseTextLabel | InputBaseIconLabel | InputBaseIconAction;

interface InputBaseTextLabel {
  position: "left" | "right";
  type: "text";
  label: string;
}

interface InputBaseIconLabel {
  position: "left" | "right";
  type: "icon";
  icon: JSX.Element;
}

interface InputBaseIconAction {
  position: "left" | "right";
  type: "action";
  content: JSX.Element;
}

const renderExtra = (extra: InputBaseTextLabel | InputBaseIconLabel | InputBaseIconAction, clsName: string, onLabelClick: () => void) => {
  if (extra.type === "text") {
    return <div className={classNames(clsName, "input-component-label")} onClick={onLabelClick}>{extra.label}</div>;
  }

  if (extra.type === "icon") {
    return <div className={classNames(clsName, "input-component-icon")} onClick={onLabelClick}>{extra.icon}</div>;
  }

  return <div className={classNames(clsName, "input-component-action")}>{extra.content}</div>;
};

export const InputBase = forwardRef<HTMLInputElement, InputBaseProps>((props, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => inputRef.current);

  useEffect(() => {
    if (props.focusOnMount) {
      focusOnMount(inputRef.current);
    }
  }, []);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      props.onEsc?.();
    } else if (e.key === "Enter") {
      props.onEnter?.();
    }

    props.onKeyDown?.(e);
  };

  const extra = props.rightComponent;

  // Labels and icons act like the input itself, so for example the pickers also open from their icon
  const onLabelClick = props.onClick && !props.disabled
    ? () => {
      inputRef.current.focus();
      props.onClick();
    }
    : undefined;

  return <div className="input-component-container" style={{ width: props.width }}>
    <div className="input-component-wrapper">
      {extra?.position === "left" && renderExtra(extra, "input-component-left", onLabelClick)}

      <input
        ref={inputRef}
        className={classNames("input-component", {
          "input-component-error": Boolean(props.error),
          "input-component-with-left": extra?.position === "left",
          "input-component-with-right": extra?.position === "right" || (props.clearable && props.value !== "") || props.loading,
        })}
        type={props.type ?? "text"}
        value={props.value}
        onChange={e => props.onChange(e.target.value)}
        maxLength={props.maxLength}
        placeholder={props.placeholder}
        disabled={props.disabled}
        onBlur={() => props.onBlur?.(props.value)}
        onFocus={props.onFocus}
        onClick={props.onClick}
        onKeyDown={onKeyDown}
        onPaste={props.onPaste}
        id={props.id}
        name={props.name}
        autoComplete={props.autoComplete}
        autoCapitalize={props.autoCapitalize}
      />

      {props.clearable && props.value !== "" && <div className="input-component-clear">
        <PhosphorXButton layout="none" onClick={props.onClear} />
      </div>}

      {extra?.position === "right" && renderExtra(extra, "input-component-right", onLabelClick)}

      {props.loading && <div className="input-component-loading"><Loading size="tiny" /></div>}
    </div>

    {typeof props.error === "string" && props.error !== "" && <div className="input-component-error-message">{props.error}</div>}
  </div>;
});
